/**
 * Pathway <-> 16-folder care-flow-component bundle serializer
 * (Sprint 4.3.4, PROD-653).
 *
 * Pure functions. No clocks. No randomness. The forward pass
 * (`pathwayToCareFlowComponents`) emits a deterministic 16-folder
 * bundle from the in-memory `EditablePathway`; the inverse pass
 * (`careFlowComponentsToPathway`) reconstructs the pathway. Round-trip
 * is byte-identical when serialised through `JSON.stringify` with the
 * same key order (HC1 reproducibility).
 *
 * Mapping rules:
 *
 *   pathway.id                           -> pathways/<id>.json
 *   nodes[nodeType=track]                -> tracks/<id>.json
 *   nodes[nodeType=step]                 -> steps/<id>.json
 *   nodes[nodeType=action]               -> actions/<id>.json
 *   edges connecting step->step          -> transitions/<id>.json
 *
 * The legacy 6 activity types (demographics / vitals / labs / decision
 * / nba / outcome) live inside the actions folder for the prototype:
 * each is emitted as a `system` action with a synthesised id + a
 * trigger sidecar that snapshots the legacy fields (lab basket /
 * state machine / linked NBAs) so the inverse pass can reconstruct
 * the original node verbatim. The other 12 folders start empty;
 * authoring through the JSON detail view fills them.
 *
 * HC5: zero em / en dashes anywhere.
 */
import type {
  EditablePathway,
  FormQuestion,
  PathwayEdge,
  PathwayNode,
  PathwayRule,
} from '../../state/pathway-types';
import type { LabBasketItem } from '../../types/lab-basket';
import {
  CARE_FLOW_FOLDERS,
  ZERO_CLOCK_ISO,
  emptyCareFlowBundle,
  type ActionComponentJson,
  type CareFlowComponentBundle,
  type CareFlowFolder,
  type FormComponentJson,
  type PathwayComponentJson,
  type QuestionComponentJson,
  type RuleComponentJson,
  type StepComponentJson,
  type TrackComponentJson,
  type TransitionComponentJson,
} from './types';

export type CareFlowComponentExtras = Partial<CareFlowComponentBundle>;

function trackIdFor(pathwayId: string, nodeId: string): string {
  return `track-${pathwayId}-${nodeId}`;
}

function stepIdFor(pathwayId: string, nodeId: string): string {
  return `step-${pathwayId}-${nodeId}`;
}

function actionIdFor(pathwayId: string, nodeId: string): string {
  return `action-${pathwayId}-${nodeId}`;
}

function transitionIdFor(pathwayId: string, edge: PathwayEdge): string {
  return `transition-${pathwayId}-${edge.id}`;
}

/**
 * Sprint 4.3.10: stable id helpers for rule records. Transition rules
 * land under `rule-<pathwayId>-transition-<stepNodeId>-<rule.id>` so the
 * inverse pass can locate the host step without a back-pointer; form-
 * question rules land under `rule-<pathwayId>-form-<formNodeId>-
 * <questionId>-<rule.id>` for the same reason.
 */
function transitionRuleIdFor(
  pathwayId: string,
  stepNodeId: string,
  rule: PathwayRule,
): string {
  return `rule-${pathwayId}-transition-${stepNodeId}-${rule.id}`;
}
function formQuestionRuleIdFor(
  pathwayId: string,
  formNodeId: string,
  questionId: string,
  rule: PathwayRule,
): string {
  return `rule-${pathwayId}-form-${formNodeId}-${questionId}-${rule.id}`;
}

/**
 * Sprint 4.3.10: build a human-readable mirror expression for a rule.
 * Kept tiny + deterministic; the structured fields on the rule
 * component are the authoritative round-trip source.
 */
function ruleExpressionFor(rule: PathwayRule): string {
  if (rule.operator === 'is-empty') {
    return `${rule.dataPointDefinitionId} is empty`;
  }
  if (rule.operator === 'is-not-empty') {
    return `${rule.dataPointDefinitionId} is not empty`;
  }
  if (rule.operator === 'in' || rule.operator === 'not-in') {
    const arr = Array.isArray(rule.value) ? rule.value : [];
    return `${rule.dataPointDefinitionId} ${rule.operator} (${arr.join(', ')})`;
  }
  return `${rule.dataPointDefinitionId} ${rule.operator} ${String(rule.value ?? '')}`;
}

function ruleToComponent(
  ruleId: string,
  rule: PathwayRule,
  attachedTo: 'transition' | 'form-question',
  attachedToId: string,
  formQuestionParentNodeId?: string,
): RuleComponentJson {
  const component: RuleComponentJson = {
    id: ruleId,
    label: rule.name,
    version: 'v1',
    lastModified: ZERO_CLOCK_ISO,
    expression: ruleExpressionFor(rule),
    attachedTo,
    attachedToId,
    dataPointDefinitionId: rule.dataPointDefinitionId,
    operator: rule.operator,
  };
  if (formQuestionParentNodeId !== undefined) {
    component.formQuestionParentNodeId = formQuestionParentNodeId;
  }
  if (rule.value !== undefined) {
    component.value = Array.isArray(rule.value) ? [...rule.value] : rule.value;
  }
  if (rule.riskTier !== undefined) {
    component.riskTier = rule.riskTier;
  }
  return component;
}

function ruleFromComponent(
  component: RuleComponentJson,
  fallbackId: string,
): PathwayRule {
  // The rule's domain id is encoded as the trailing segment of the
  // component id so the inverse round-trip reconstructs the original
  // PathwayRule.id exactly. The component id is stable but we never
  // ship a free-form id field on RuleComponentJson, so derive it from
  // the convention.
  let value: PathwayRule['value'];
  const compValue = component.value;
  if (compValue === undefined) {
    value = undefined;
  } else if (Array.isArray(compValue)) {
    value = (compValue as ReadonlyArray<string>).map((s) => String(s));
  } else if (typeof compValue === 'number') {
    value = compValue;
  } else {
    value = compValue as string;
  }
  return {
    id: fallbackId,
    name: component.label,
    dataPointDefinitionId: component.dataPointDefinitionId ?? '',
    operator: component.operator ?? 'equals',
    value,
    riskTier: component.riskTier,
  };
}

/** Sprint 4.3.9: stable id helpers for form + question records. */
function formIdFor(pathwayId: string, nodeId: string): string {
  return `form-${pathwayId}-${nodeId}`;
}
function questionIdFor(
  pathwayId: string,
  nodeId: string,
  questionId: string,
): string {
  return `question-${pathwayId}-${nodeId}-${questionId}`;
}

const FORM_QUESTION_TYPE_TO_QUESTION_TYPE: Record<
  FormQuestion['type'],
  QuestionComponentJson['questionType']
> = {
  text: 'short-text',
  number: 'number',
  date: 'date',
  single_choice: 'single-choice',
  multiple_choice: 'multi-choice',
  scale: 'scale',
};

const QUESTION_TYPE_TO_FORM_QUESTION_TYPE: Partial<
  Record<QuestionComponentJson['questionType'], FormQuestion['type']>
> = {
  'short-text': 'text',
  'long-text': 'text',
  number: 'number',
  date: 'date',
  'single-choice': 'single_choice',
  'multi-choice': 'multiple_choice',
  scale: 'scale',
  // boolean falls back to text on inverse so the round-trip stays
  // information-preserving for the form-builder vocabulary; legacy
  // boolean questions remain decodable but lose the boolean tag (which
  // the form-builder does not surface in the editor).
  boolean: 'text',
};

interface LegacyActivityPayload {
  legacyType:
    | 'demographics'
    | 'vitals'
    | 'labs'
    | 'decision'
    | 'nba'
    | 'outcome';
  description?: string;
  requiresConfirmation?: boolean;
  stateMachine?: string;
  linkedNbaIds?: ReadonlyArray<string>;
  labBasketItems?: ReadonlyArray<LabBasketItem>;
}

const LEGACY_TYPES: ReadonlyArray<LegacyActivityPayload['legacyType']> = [
  'demographics',
  'vitals',
  'labs',
  'decision',
  'nba',
  'outcome',
];

function isLegacyType(t: string): t is LegacyActivityPayload['legacyType'] {
  return LEGACY_TYPES.includes(t as LegacyActivityPayload['legacyType']);
}

/**
 * Forward pass. Pure function. Deterministic. Stable in input order.
 */
export function pathwayToCareFlowComponents(
  pathway: EditablePathway,
  extras: CareFlowComponentExtras = {},
): CareFlowComponentBundle {
  const bundle = emptyCareFlowBundle();

  const tracks: TrackComponentJson[] = [];
  const steps: StepComponentJson[] = [];
  const actions: ActionComponentJson[] = [];
  const transitions: TransitionComponentJson[] = [];

  const trackNodes = pathway.nodes.filter((n) => n.nodeType === 'track');
  const stepNodes = pathway.nodes.filter((n) => n.nodeType === 'step');
  const actionNodes = pathway.nodes.filter((n) => n.nodeType === 'action');
  const legacyNodes = pathway.nodes.filter((n) => isLegacyType(n.nodeType));

  const stepIdsByTrack = new Map<string, string[]>();
  for (const s of stepNodes) {
    if (!s.parentNodeId) continue;
    const arr = stepIdsByTrack.get(s.parentNodeId) ?? [];
    arr.push(stepIdFor(pathway.pathwayId, s.id));
    stepIdsByTrack.set(s.parentNodeId, arr);
  }
  const actionIdsByStep = new Map<string, string[]>();
  for (const a of actionNodes) {
    if (!a.parentNodeId) continue;
    const arr = actionIdsByStep.get(a.parentNodeId) ?? [];
    arr.push(actionIdFor(pathway.pathwayId, a.id));
    actionIdsByStep.set(a.parentNodeId, arr);
  }

  for (const t of trackNodes) {
    tracks.push({
      id: trackIdFor(pathway.pathwayId, t.id),
      label: t.label,
      version: 'v1',
      lastModified: ZERO_CLOCK_ISO,
      pathwayId: pathway.pathwayId,
      stepIds: stepIdsByTrack.get(t.id) ?? [],
      triggerType: t.triggerType,
      triggerFilter: t.triggerFilter,
    });
  }

  for (const s of stepNodes) {
    if (!s.parentNodeId) continue;
    steps.push({
      id: stepIdFor(pathway.pathwayId, s.id),
      label: s.label,
      version: 'v1',
      lastModified: ZERO_CLOCK_ISO,
      trackId: trackIdFor(pathway.pathwayId, s.parentNodeId),
      actionIds: actionIdsByStep.get(s.id) ?? [],
      isDecision: s.isDecision,
    });
  }

  const forms: FormComponentJson[] = [];
  const questions: QuestionComponentJson[] = [];
  // Sprint 4.3.10: rules folder. Both transition rules (attached to
  // step nodes) and form-question display rules (attached to per-
  // question entries) emit into the same `rules/` folder.
  const rules: RuleComponentJson[] = [];
  for (const a of actionNodes) {
    let formId: string | undefined;
    if (a.actionType === 'form') {
      formId = formIdFor(pathway.pathwayId, a.id);
      const formQuestions = a.formQuestions ?? [];
      const questionIds: string[] = [];
      for (const q of formQuestions) {
        const qId = questionIdFor(pathway.pathwayId, a.id, q.id);
        questionIds.push(qId);
        const qComp: QuestionComponentJson = {
          id: qId,
          label: q.text,
          version: 'v1',
          lastModified: ZERO_CLOCK_ISO,
          questionType: FORM_QUESTION_TYPE_TO_QUESTION_TYPE[q.type],
          required: q.required,
        };
        if (q.options !== undefined && q.options.length > 0) {
          qComp.optionEntries = q.options.map((opt) => ({
            value: opt.value,
            label: opt.label,
          }));
          qComp.options = q.options.map((opt) => opt.value);
        }
        if (q.dataPointDefinitionId !== undefined) {
          qComp.dataPointDefinitionId = q.dataPointDefinitionId;
        }
        if (q.scaleMin !== undefined) qComp.scaleMin = q.scaleMin;
        if (q.scaleMax !== undefined) qComp.scaleMax = q.scaleMax;
        questions.push(qComp);
        // Sprint 4.3.10: form-question display rule emits into the
        // rules folder + the question record references it via
        // `ruleId` so the inverse pass can rejoin the pair.
        if (q.enableWhenRule !== undefined) {
          const ruleId = formQuestionRuleIdFor(
            pathway.pathwayId,
            a.id,
            q.id,
            q.enableWhenRule,
          );
          rules.push(
            ruleToComponent(
              ruleId,
              q.enableWhenRule,
              'form-question',
              qId,
              actionIdFor(pathway.pathwayId, a.id),
            ),
          );
          (qComp as { ruleId?: string }).ruleId = ruleId;
        }
      }
      forms.push({
        id: formId,
        label: a.label,
        version: 'v1',
        lastModified: ZERO_CLOCK_ISO,
        questionIds,
        stakeholderId: a.assignedStakeholderId,
      });
    }
    actions.push({
      id: actionIdFor(pathway.pathwayId, a.id),
      label: a.label,
      version: 'v1',
      lastModified: ZERO_CLOCK_ISO,
      category: a.category ?? 'user',
      stepId: a.parentNodeId
        ? stepIdFor(pathway.pathwayId, a.parentNodeId)
        : undefined,
      formId,
    });
  }
  // Sprint 4.3.10: transition rules emit AFTER step nodes are walked so
  // the rule id contains the step's bundle-prefixed id. The transition
  // record set is rebuilt below; we pre-collect the rule -> stepId map
  // here so transition records can reference the rule id.
  const transitionRulesByStepId = new Map<string, RuleComponentJson>();
  for (const s of stepNodes) {
    if (s.transitionRule === undefined) continue;
    const ruleId = transitionRuleIdFor(
      pathway.pathwayId,
      s.id,
      s.transitionRule,
    );
    const rule = ruleToComponent(
      ruleId,
      s.transitionRule,
      'transition',
      stepIdFor(pathway.pathwayId, s.id),
    );
    rules.push(rule);
    transitionRulesByStepId.set(s.id, rule);
  }

  for (const l of legacyNodes) {
    const payload: LegacyActivityPayload = {
      legacyType: l.nodeType as LegacyActivityPayload['legacyType'],
      description: l.description,
      requiresConfirmation: l.requiresConfirmation,
      stateMachine: l.stateMachine,
      linkedNbaIds: l.linkedNbaIds,
      labBasketItems: l.labBasketItems,
    };
    actions.push({
      id: actionIdFor(pathway.pathwayId, l.id),
      label: l.label,
      version: 'v1',
      lastModified: ZERO_CLOCK_ISO,
      category: 'system',
    });
    bundle.triggers = [
      ...bundle.triggers,
      {
        id: `legacy-${actionIdFor(pathway.pathwayId, l.id)}`,
        label: `Legacy ${l.nodeType} payload for ${l.id}`,
        version: 'v1',
        lastModified: ZERO_CLOCK_ISO,
        kind: 'manual',
        configuration: {
          actionId: actionIdFor(pathway.pathwayId, l.id),
          legacy: payload,
        },
      },
    ];
  }

  const stepIdSet = new Set(stepNodes.map((n) => n.id));
  for (const e of pathway.edges) {
    if (stepIdSet.has(e.source) && stepIdSet.has(e.target)) {
      const transition: TransitionComponentJson = {
        id: transitionIdFor(pathway.pathwayId, e),
        label: e.label ?? `${e.source} -> ${e.target}`,
        version: 'v1',
        lastModified: ZERO_CLOCK_ISO,
        fromStepId: stepIdFor(pathway.pathwayId, e.source),
        toStepId: stepIdFor(pathway.pathwayId, e.target),
      };
      // Sprint 4.3.10: hang the source-step transition rule (if any)
      // off the transition record via the existing `ruleId` field so a
      // FHIR consumer browsing the bundle without the rule extension
      // still sees the relationship.
      const srcRule = transitionRulesByStepId.get(e.source);
      if (srcRule !== undefined) {
        transition.ruleId = srcRule.id;
      }
      transitions.push(transition);
    }
  }

  const pathwayComponent: PathwayComponentJson = {
    id: pathway.pathwayId,
    label: pathway.pathwayId,
    version: 'v1',
    lastModified: ZERO_CLOCK_ISO,
    tenantId: pathway.tenantId,
    trackIds: tracks.map((t) => t.id),
  };

  bundle.pathways = [pathwayComponent];
  bundle.tracks = tracks;
  bundle.steps = steps;
  bundle.actions = actions;
  bundle.transitions = transitions;
  // Sprint 4.3.9: forms + questions bundle from form-type action nodes.
  bundle.forms = forms;
  bundle.questions = questions;
  // Sprint 4.3.10: rules bundle. Both transition rules (attached to
  // step nodes) and form-question display rules (attached to per-
  // question entries) live here.
  bundle.rules = rules;

  for (const folder of CARE_FLOW_FOLDERS) {
    if (folder === 'pathways') continue;
    if (folder === 'tracks') continue;
    if (folder === 'steps') continue;
    if (folder === 'actions') continue;
    if (folder === 'transitions') continue;
    if (folder === 'triggers') continue;
    if (folder === 'forms') continue;
    if (folder === 'questions') continue;
    if (folder === 'rules') continue;
    const next = extras[folder];
    if (next !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (bundle as any)[folder] = [...next];
    }
  }
  if (extras.triggers !== undefined) {
    bundle.triggers = [...bundle.triggers, ...extras.triggers];
  }
  if (extras.forms !== undefined) {
    bundle.forms = [...bundle.forms, ...extras.forms];
  }
  if (extras.questions !== undefined) {
    bundle.questions = [...bundle.questions, ...extras.questions];
  }
  if (extras.rules !== undefined) {
    bundle.rules = [...bundle.rules, ...extras.rules];
  }

  return bundle;
}

/**
 * Inverse pass. Pure function. Reconstructs `EditablePathway` from a
 * 16-folder bundle. Round-trip is byte-identical when the inputs come
 * from `pathwayToCareFlowComponents` with the same `extras`.
 */
export function careFlowComponentsToPathway(
  bundle: CareFlowComponentBundle,
): EditablePathway {
  const pathwayComponent = bundle.pathways[0];
  if (!pathwayComponent) {
    throw new Error(
      'careFlowComponentsToPathway: bundle.pathways must contain at least one pathway component',
    );
  }

  const trackPrefix = `track-${pathwayComponent.id}-`;
  const stepPrefix = `step-${pathwayComponent.id}-`;
  const actionPrefix = `action-${pathwayComponent.id}-`;
  const transitionPrefix = `transition-${pathwayComponent.id}-`;

  const legacyPayloadsByActionId = new Map<string, LegacyActivityPayload>();
  for (const trig of bundle.triggers) {
    const cfg = trig.configuration as
      | { actionId?: string; legacy?: LegacyActivityPayload }
      | undefined;
    if (cfg?.actionId && cfg.legacy) {
      legacyPayloadsByActionId.set(cfg.actionId, cfg.legacy);
    }
  }

  const nodes: PathwayNode[] = [];

  for (const t of bundle.tracks) {
    if (t.pathwayId !== pathwayComponent.id) continue;
    const id = t.id.startsWith(trackPrefix)
      ? t.id.slice(trackPrefix.length)
      : t.id;
    nodes.push({
      id,
      nodeType: 'track',
      label: t.label,
      triggerType: t.triggerType,
      triggerFilter: t.triggerFilter,
    });
  }
  // Sprint 4.3.10: index rules by attachedToId for fast lookup during
  // node reconstruction.
  const transitionRulesByPrefixedStepId = new Map<string, RuleComponentJson>();
  const formQuestionRulesByPrefixedQuestionId = new Map<
    string,
    RuleComponentJson
  >();
  for (const r of bundle.rules) {
    if (r.attachedTo === 'transition' && r.attachedToId !== undefined) {
      transitionRulesByPrefixedStepId.set(r.attachedToId, r);
    } else if (
      r.attachedTo === 'form-question' &&
      r.attachedToId !== undefined
    ) {
      formQuestionRulesByPrefixedQuestionId.set(r.attachedToId, r);
    }
  }
  for (const s of bundle.steps) {
    const id = s.id.startsWith(stepPrefix)
      ? s.id.slice(stepPrefix.length)
      : s.id;
    const trackOriginalId = s.trackId.startsWith(trackPrefix)
      ? s.trackId.slice(trackPrefix.length)
      : s.trackId;
    const stepNode: PathwayNode = {
      id,
      nodeType: 'step',
      label: s.label,
      parentNodeId: trackOriginalId,
      isDecision: s.isDecision,
    };
    // Sprint 4.3.10: re-attach a transition rule from the bundle when
    // one was authored against this step. The rule id encodes the
    // original PathwayRule.id as the trailing segment so the inverse
    // round-trip restores the rule verbatim.
    const ruleComponent = transitionRulesByPrefixedStepId.get(s.id);
    if (ruleComponent !== undefined) {
      const expectedPrefix = `rule-${pathwayComponent.id}-transition-${id}-`;
      const ruleDomainId = ruleComponent.id.startsWith(expectedPrefix)
        ? ruleComponent.id.slice(expectedPrefix.length)
        : ruleComponent.id;
      stepNode.transitionRule = ruleFromComponent(ruleComponent, ruleDomainId);
    }
    nodes.push(stepNode);
  }
  for (const a of bundle.actions) {
    const id = a.id.startsWith(actionPrefix)
      ? a.id.slice(actionPrefix.length)
      : a.id;
    const legacy = legacyPayloadsByActionId.get(a.id);
    if (legacy) {
      nodes.push({
        id,
        nodeType: legacy.legacyType,
        label: a.label,
        description: legacy.description,
        requiresConfirmation: legacy.requiresConfirmation,
        stateMachine: legacy.stateMachine,
        linkedNbaIds: legacy.linkedNbaIds
          ? [...legacy.linkedNbaIds]
          : undefined,
        labBasketItems: legacy.labBasketItems
          ? legacy.labBasketItems.map((i) => ({ ...i }))
          : undefined,
      });
      continue;
    }
    const stepOriginalId = a.stepId
      ? a.stepId.startsWith(stepPrefix)
        ? a.stepId.slice(stepPrefix.length)
        : a.stepId
      : undefined;
    // Sprint 4.3.9: reconstruct form questions when the action carries a
    // form id pointing into the bundle's forms folder.
    let actionType: PathwayNode['actionType'];
    let formQuestions: FormQuestion[] | undefined;
    if (a.formId !== undefined) {
      const form = bundle.forms.find((f) => f.id === a.formId);
      if (form !== undefined) {
        actionType = 'form';
        formQuestions = form.questionIds
          .map((qId) => bundle.questions.find((q) => q.id === qId))
          .filter((q): q is QuestionComponentJson => q !== undefined)
          .map((q) => {
            const t = QUESTION_TYPE_TO_FORM_QUESTION_TYPE[q.questionType];
            const fq: FormQuestion = {
              // The original (pre-prefix) question id mirrors the in-
              // memory FormQuestion.id; strip the bundle prefix so a
              // round-trip is byte-stable.
              id: q.id.startsWith(`question-${pathwayComponent.id}-${id}-`)
                ? q.id.slice(`question-${pathwayComponent.id}-${id}-`.length)
                : q.id,
              text: q.label,
              type: t ?? 'text',
              required: q.required ?? false,
            };
            if (q.optionEntries !== undefined && q.optionEntries.length > 0) {
              fq.options = q.optionEntries.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }));
            } else if (q.options !== undefined && q.options.length > 0) {
              fq.options = q.options.map((opt) => ({
                value: opt,
                label: opt,
              }));
            }
            if (q.dataPointDefinitionId !== undefined) {
              fq.dataPointDefinitionId = q.dataPointDefinitionId;
            }
            if (q.scaleMin !== undefined) fq.scaleMin = q.scaleMin;
            if (q.scaleMax !== undefined) fq.scaleMax = q.scaleMax;
            // Sprint 4.3.10: re-attach the form-question display rule
            // when the question record points at one. The rule id
            // encodes the original PathwayRule.id as the trailing
            // segment so the inverse pass restores it verbatim.
            const ruleComponent = formQuestionRulesByPrefixedQuestionId.get(
              q.id,
            );
            if (ruleComponent !== undefined) {
              const expectedPrefix = `rule-${pathwayComponent.id}-form-${id}-${fq.id}-`;
              const ruleDomainId = ruleComponent.id.startsWith(expectedPrefix)
                ? ruleComponent.id.slice(expectedPrefix.length)
                : ruleComponent.id;
              fq.enableWhenRule = ruleFromComponent(
                ruleComponent,
                ruleDomainId,
              );
            }
            return fq;
          });
      }
    }
    const node: PathwayNode = {
      id,
      nodeType: 'action',
      label: a.label,
      parentNodeId: stepOriginalId,
      category: a.category,
    };
    if (actionType !== undefined) node.actionType = actionType;
    if (formQuestions !== undefined) node.formQuestions = formQuestions;
    nodes.push(node);
  }

  const edges: PathwayEdge[] = [];
  for (const tr of bundle.transitions) {
    const id = tr.id.startsWith(transitionPrefix)
      ? tr.id.slice(transitionPrefix.length)
      : tr.id;
    const fromOriginal = tr.fromStepId.startsWith(stepPrefix)
      ? tr.fromStepId.slice(stepPrefix.length)
      : tr.fromStepId;
    const toOriginal = tr.toStepId.startsWith(stepPrefix)
      ? tr.toStepId.slice(stepPrefix.length)
      : tr.toStepId;
    edges.push({
      id,
      source: fromOriginal,
      target: toOriginal,
      label: tr.label,
    });
  }

  return {
    id: pathwayComponent.id,
    pathwayId: pathwayComponent.id as EditablePathway['pathwayId'],
    tenantId: pathwayComponent.tenantId,
    nodes,
    edges,
  };
}

/**
 * Strict-key JSON serialisation. Emits the bundle as a record of
 * folder-name -> JSON.stringify(componentArray) so two equal bundles
 * produce byte-identical strings (HC1 reproducibility).
 */
export function bundleToFileMap(
  bundle: CareFlowComponentBundle,
): Record<string, string> {
  const fileMap: Record<string, string> = {};
  for (const folder of CARE_FLOW_FOLDERS) {
    fileMap[`${folder}/${folder}.json`] = JSON.stringify(
      bundle[folder],
      null,
      2,
    );
  }
  return fileMap;
}

export interface BundleDiffEntry {
  path: string;
  status: 'added' | 'modified' | 'unchanged' | 'removed';
  beforeBytes: number;
  afterBytes: number;
}

export function diffFileMaps(
  before: Record<string, string>,
  after: Record<string, string>,
): ReadonlyArray<BundleDiffEntry> {
  const paths = new Set<string>([
    ...Object.keys(before),
    ...Object.keys(after),
  ]);
  const entries: BundleDiffEntry[] = [];
  for (const path of [...paths].sort()) {
    const b = before[path];
    const a = after[path];
    if (b === undefined && a !== undefined) {
      entries.push({
        path,
        status: 'added',
        beforeBytes: 0,
        afterBytes: a.length,
      });
      continue;
    }
    if (a === undefined && b !== undefined) {
      entries.push({
        path,
        status: 'removed',
        beforeBytes: b.length,
        afterBytes: 0,
      });
      continue;
    }
    if (a === b) {
      entries.push({
        path,
        status: 'unchanged',
        beforeBytes: b?.length ?? 0,
        afterBytes: a?.length ?? 0,
      });
    } else {
      entries.push({
        path,
        status: 'modified',
        beforeBytes: b?.length ?? 0,
        afterBytes: a?.length ?? 0,
      });
    }
  }
  return entries;
}

export function formatDiffSummary(
  entries: ReadonlyArray<BundleDiffEntry>,
): string {
  if (entries.length === 0) return '(empty bundle)';
  return entries
    .map((e) => {
      const sigil =
        e.status === 'added'
          ? 'A'
          : e.status === 'modified'
            ? 'M'
            : e.status === 'removed'
              ? 'D'
              : ' ';
      return `${sigil} ${e.path} (${e.beforeBytes} -> ${e.afterBytes} bytes)`;
    })
    .join('\n');
}

export function countComponentsByFolder(
  bundle: CareFlowComponentBundle,
): Record<CareFlowFolder, number> {
  return CARE_FLOW_FOLDERS.reduce(
    (acc, folder) => {
      acc[folder] = bundle[folder].length;
      return acc;
    },
    {} as Record<CareFlowFolder, number>,
  );
}
